import { Check, ChevronDown, Search } from 'lucide-react';
import {
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { Field } from './field';
import { Spinner } from './feedback';
import { cn } from '@/lib/utils';

export type OptionCombobox<T> = {
  cle: string;
  libelle: string;
  description?: string;
  valeur: T;
};

/**
 * Champ de saisie avec suggestions. La saisie libre reste possible : sur
 * l'écran Commandes, taper un nom inconnu crée le client (règle 12), il ne faut
 * donc jamais forcer une sélection dans la liste.
 */
export function Combobox<T>({
  label,
  aide,
  erreur,
  requis,
  valeur,
  onChangeTexte,
  onSelectionner,
  options,
  chargement,
  placeholder,
  className,
  videMessage = 'Aucun résultat',
  autoriserSaisieLibre = true,
  pied,
}: {
  label?: string;
  aide?: string;
  erreur?: string;
  requis?: boolean;
  /** Texte affiché dans le champ (contrôlé par le parent). */
  valeur: string;
  onChangeTexte: (texte: string) => void;
  onSelectionner: (option: OptionCombobox<T>) => void;
  options: readonly OptionCombobox<T>[];
  chargement?: boolean;
  placeholder?: string;
  className?: string;
  videMessage?: string;
  autoriserSaisieLibre?: boolean;
  /** Contenu ajouté sous la liste (par exemple « Créer ce client »). */
  pied?: ReactNode;
}) {
  const id = useId();
  const [ouvert, setOuvert] = useState(false);
  const [indexActif, setIndexActif] = useState(0);
  const conteneur = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const surClicExterieur = (e: MouseEvent) => {
      if (!conteneur.current?.contains(e.target as Node)) setOuvert(false);
    };
    document.addEventListener('mousedown', surClicExterieur);
    return () => document.removeEventListener('mousedown', surClicExterieur);
  }, []);

  const visibles = useMemo(() => options.slice(0, 12), [options]);

  function choisir(option: OptionCombobox<T>) {
    onSelectionner(option);
    setOuvert(false);
  }

  function surTouche(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setOuvert(true);
      setIndexActif((i) => Math.min(i + 1, visibles.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setIndexActif((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter' && ouvert) {
      const option = visibles[indexActif];
      if (option) {
        e.preventDefault();
        choisir(option);
      }
    } else if (e.key === 'Escape') {
      setOuvert(false);
    }
  }

  return (
    <Field
      label={label}
      aide={aide}
      erreur={erreur}
      requis={requis}
      className={className}
      htmlFor={id}
    >
      <div ref={conteneur} className="relative">
        <div className="relative">
          <input
            id={id}
            value={valeur}
            onChange={(e) => {
              onChangeTexte(e.target.value);
              setOuvert(true);
              setIndexActif(0);
            }}
            onFocus={() => setOuvert(true)}
            onKeyDown={surTouche}
            placeholder={placeholder}
            autoComplete="off"
            role="combobox"
            aria-expanded={ouvert}
            aria-controls={`${id}-liste`}
            readOnly={!autoriserSaisieLibre}
            className={cn('champ pr-9', erreur && 'champ-erreur')}
          />
          <span className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 text-ink-muted">
            {chargement ? <Spinner /> : <ChevronDown aria-hidden className="h-4 w-4" />}
          </span>
        </div>

        {ouvert ? (
          <div
            id={`${id}-liste`}
            role="listbox"
            className="absolute z-30 mt-1 max-h-64 w-full animate-fade-in overflow-y-auto
              rounded-md border border-line bg-white py-1 shadow-pop"
          >
            {visibles.length === 0 ? (
              <p className="flex items-center gap-2 px-3 py-2 text-sm text-ink-muted">
                <Search aria-hidden className="h-3.5 w-3.5" />
                {videMessage}
              </p>
            ) : (
              visibles.map((option, i) => (
                <button
                  key={option.cle}
                  type="button"
                  role="option"
                  aria-selected={i === indexActif}
                  onMouseEnter={() => setIndexActif(i)}
                  onClick={() => choisir(option)}
                  className={cn(
                    'flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-sm',
                    i === indexActif ? 'bg-mustard-50 text-ink' : 'text-ink-soft',
                  )}
                >
                  <span className="min-w-0">
                    <span className="block truncate font-medium text-ink">{option.libelle}</span>
                    {option.description ? (
                      <span className="block truncate text-xs text-ink-muted">
                        {option.description}
                      </span>
                    ) : null}
                  </span>
                  {option.libelle === valeur ? (
                    <Check aria-hidden className="h-4 w-4 shrink-0 text-mustard-600" />
                  ) : null}
                </button>
              ))
            )}
            {pied ? <div className="border-t border-line px-3 py-2">{pied}</div> : null}
          </div>
        ) : null}
      </div>
    </Field>
  );
}
